import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { create } from 'zustand';

/* ─── Store (self-contained — no external dependency) ─────────── */

export interface ToastItem {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning';
  duration: number;
}

interface ToastState {
  toasts: readonly ToastItem[];
  addToast: (message: string, type?: ToastItem['type'], duration?: number) => void;
  removeToast: (id: string) => void;
}

let _counter = 0;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  addToast: (message, type = 'info', duration = 2500) => {
    const id = `toast-${++_counter}`;
    set((s) => ({ toasts: [...s.toasts.slice(-4), { id, message, type, duration }] }));
  },
  removeToast: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
}));

/** Convenience global — importable from anywhere without a hook. */
export function toast(message: string, type?: ToastItem['type']): void {
  useToastStore.getState().addToast(message, type);
}

/* ─── Renderer ────────────────────────────────────────────────── */

const TYPE_BORDER: Record<ToastItem['type'], string> = {
  info: 'var(--border-mid)',
  success: 'rgba(112, 148, 36, 0.5)',
  warning: 'rgba(212, 160, 23, 0.5)',
};

export default function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);

  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 900,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 'var(--space-sm)',
        pointerEvents: 'none',
      }}
    >
      <AnimatePresence>
        {toasts.map((t) => (
          <ToastChip key={t.id} item={t} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function ToastChip({ item }: { item: ToastItem }) {
  const remove = useToastStore((s) => s.removeToast);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    timerRef.current = setTimeout(() => remove(item.id), item.duration);
    return () => clearTimeout(timerRef.current);
  }, [item.id, item.duration, remove]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 4, scale: 0.95 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      style={{
        padding: '8px 14px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-panel-2)',
        border: `1px solid ${TYPE_BORDER[item.type]}`,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
        fontFamily: 'var(--font-sans)',
        fontSize: 12,
        fontWeight: 500,
        color: 'var(--text-primary)',
        pointerEvents: 'auto',
        maxWidth: 300,
        lineHeight: 1.4,
      }}
    >
      {item.message}
    </motion.div>
  );
}
